import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiCpu, FiRocket } = FiIcons;

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-x-hidden bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 pb-20">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0
            }}
            animate={{
              x: [null, Math.random() * window.innerWidth],
              y: [null, Math.random() * window.innerHeight],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: Math.random() * 10 + 5,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <img 
            src="https://d268zs2sdbzvo0.cloudfront.net/22144dca-44c9-40e5-bcaf-c785db45f372.png" 
            alt="Next Level Local" 
            className="w-32 h-auto mx-auto mb-8"
          />
        </motion.div>

        <motion.h1 
          className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Don't Settle For{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-500 animate-pulse">
            Basic Tools
          </span>
          <br />
          <span className="text-4xl md:text-6xl">
            Elevate Your Business With{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              Next Level Premium AI!
            </span>
          </span>
        </motion.h1>

        <motion.p 
          className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          🚀 <span className="font-bold text-yellow-400">Unlock Your Creativity</span> with NextLevel Assistant: 
          <span className="font-bold text-pink-400"> Affordable Memberships</span> Tailored for Every Dream!
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
        >
          <motion.a
            href="https://buy.stripe.com/4gMeVe5Ep3Ml3CK9bk6Vq04"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-12 py-6 bg-gradient-to-r from-yellow-400 to-pink-500 text-black text-xl font-black rounded-full transform transition-all duration-300 hover:scale-110 hover:rotate-1"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center gap-3">
              <SafeIcon icon={FiRocket} className="w-6 h-6" />
              Start Your Free Trial
              <SafeIcon icon={FiZap} className="w-6 h-6 animate-pulse" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
          </motion.a>
        </motion.div>

        {/* Animated Price Tiers */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          {[
            { price: "$29", name: "Playground", color: "from-green-400 to-blue-500" },
            { price: "$49", name: "Business", color: "from-blue-400 to-purple-500", popular: true },
            { price: "$79", name: "Marketer", color: "from-purple-400 to-pink-500" }
          ].map((tier, i) => (
            <motion.div
              key={tier.name}
              className={`relative p-1 rounded-2xl bg-gradient-to-br ${tier.color} transform transition-all duration-300 hover:scale-105`}
              whileHover={{ y: -10 }}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold z-20 shadow-lg">
                  MOST POPULAR
                </div>
              )}
              
              <div className="bg-gray-900 rounded-xl p-8 h-full flex flex-col justify-center relative z-10">
                <div className="text-4xl font-black text-white mb-2">{tier.price}</div>
                <div className="text-xl text-gray-300 mb-4">{tier.name}</div>
                <div className="text-sm text-gray-400">/month</div>
              </div>

              {/* No Price Increases Bubble - Attached specifically to Playground card */}
              {tier.name === 'Playground' && (
                <div className="absolute -bottom-16 left-0 right-0 flex justify-center z-0">
                  <div className="bg-gradient-to-r from-blue-400 to-cyan-500 text-white px-5 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse whitespace-nowrap">
                    No Price Increases! 🔒
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;